import React, { useEffect, useMemo, useState } from "react";
import { EventTriggers } from "@app/shared/src/api/api.gen";
import { ClientEndpoint, Device, EventTrigger, FleetDefaults, RelaySelection } from "@app/shared/src/models";
import { Card, CardNote, CardHead, FRow, Note, Select, Spinner, Tag, TextInput, useToast } from "@app/shared/src/components_v2";
import { ALERT_CATEGORIES, ALERT_CATEGORY_LABELS, AlertCategory } from "../../helpers/alertCategories";
import {
    NotificationSettings,
    applyEmailNotification,
    applyRelaySelection,
    notificationsFromDefaults,
    notifiesByEmail,
    relaySelectionOf,
    rewireRelayNumbers,
    uniqueRecipients,
} from "../../helpers/notificationSettings";
import { DEFAULT_RELAY_WIRING, RelayWiringMap, effectiveRelayWiring } from "@app/shared/src/helpers/relayWiring";
import { relayChoices } from "./NotificationsSection";
import { ApplyToFleetDialog } from "../shared/ApplyToFleetDialog";
import { SaveBar } from "../shared/SaveBar";
import { Toggle } from "../shared/Toggle";

/**
 * The organization's stated notification rules.
 *
 * Saving records the intent, then pushes it onto the triggers that were following the old value —
 * per alert, so switching speeding email on does not disturb a truck whose collision rule someone
 * set deliberately.
 *
 * Email needs an endpoint to attach to and it is per site, so a location without one cannot be
 * pushed to. Those are named rather than silently skipped: "saved everywhere except the four sites
 * that cannot email anyone" is the useful sentence.
 */

/** A card row for a free-text setting: title and hint over the input, aligned with FRow's padding. */
const ProvisionRow: React.FC<{ title: string; sub: string; children: React.ReactNode }> = ({ title, sub, children }) => (
    <div className="flex flex-col gap-2 border-t border-[var(--line)] px-5 py-3.5 first:border-t-0">
        <span>
            <span className="block text-[13.5px] font-medium">{title}</span>
            <span className="mt-0.5 block text-[12px] leading-snug text-[var(--muted)]">{sub}</span>
        </span>
        {children}
    </div>
);

export const FleetNotificationsSection: React.FC<{
    defaults?: FleetDefaults;
    /** Every trigger in the fleet with the unit it governs, grouped by alert. */
    triggerGroups: Map<AlertCategory, { trigger: EventTrigger; device: Device }[]>;
    emailEndpoints: ClientEndpoint[];
    relayEndpoints: ClientEndpoint[];
    onSave: (apply: (target: FleetDefaults) => void) => Promise<void>;
    onPushed: () => void;
}> = ({ defaults, triggerGroups, emailEndpoints, relayEndpoints, onSave, onPushed }) => {
    const toast = useToast();
    type Draft = NotificationSettings & {
        provisioning: { emailAddresses: string; relayIp: string };
        wiring: RelayWiringMap;
    };
    const saved = useMemo<Draft | undefined>(
        () =>
            defaults
                ? {
                      ...notificationsFromDefaults(defaults),
                      provisioning: {
                          emailAddresses: defaults.notificationEmailAddresses ?? "",
                          relayIp: defaults.webRelayIpAddress ?? "",
                      },
                      wiring: effectiveRelayWiring(defaults),
                  }
                : undefined,
        [defaults],
    );
    const [draft, setDraft] = useState<Draft | undefined>(saved);
    const [pending, setPending] = useState<{ targets: Device[]; run: () => Promise<void>; summary: string }>();
    const [busy, setBusy] = useState(false);

    useEffect(() => setDraft(saved), [saved]);

    if (!draft || !saved || !defaults) {
        return (
            <Card>
                <div className="grid place-items-center p-12 text-[var(--muted)]">
                    <Spinner />
                </div>
            </Card>
        );
    }

    const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
    const changed = ALERT_CATEGORIES.filter(
        (c) => draft[c].email !== saved[c].email || draft[c].relays !== saved[c].relays,
    );

    /** An endpoint on the same site as the trigger — email is per location, not per org. */
    const endpointFor = (device: Device) =>
        emailEndpoints.find((e) => e.facilityId && e.facilityId === device.facilityId);

    /** Same rule for the relay board, which a trigger with no relay join yet needs to build one. */
    const relayFor = (device: Device) =>
        relayEndpoints.find((e) => e.facilityId && e.facilityId === device.facilityId);

    /** A draft's wiring shaped like the FleetDefaults fields, so effectiveRelayWiring can layer it. */
    const fleetFieldsOf = (w: RelayWiringMap) => ({
        hornRelayNumber: w.horn.number,
        hornRelayName: w.horn.name.trim() || undefined,
        hornRelaySnoozable: w.horn.snoozable,
        lightsRelayNumber: w.lights.number,
        lightsRelayName: w.lights.name.trim() || undefined,
        lightsRelaySnoozable: w.lights.snoozable,
    });

    // Per truck: its own endpoint overrides still sit on top of whatever the fleet page says, so a
    // unit someone rewired by hand is read — and rewritten — by its actual terminals, not ours.
    const savedWiringFor = (device: Device) => effectiveRelayWiring(fleetFieldsOf(saved.wiring), relayFor(device));
    const draftWiringFor = (device: Device) => effectiveRelayWiring(fleetFieldsOf(draft.wiring), relayFor(device));

    // Labels for the pickers come from the draft, so renaming a slot re-labels the choices live.
    const fleetWiring: RelayWiringMap = {
        horn: { ...draft.wiring.horn, name: draft.wiring.horn.name.trim() || DEFAULT_RELAY_WIRING.horn.name },
        lights: { ...draft.wiring.lights, name: draft.wiring.lights.name.trim() || DEFAULT_RELAY_WIRING.lights.name },
    };

    const wiringChanged = JSON.stringify(draft.wiring) !== JSON.stringify(saved.wiring);
    const numbersChanged =
        draft.wiring.horn.number !== saved.wiring.horn.number ||
        draft.wiring.lights.number !== saved.wiring.lights.number;
    const wiringValid = draft.wiring.horn.number !== draft.wiring.lights.number;

    // Following the old value on something that changed, so this is a unit the save should move.
    const entriesToMove = changed.flatMap((category) =>
        (triggerGroups.get(category) ?? [])
            .filter(({ trigger, device }) => {
                const emailMatches = notifiesByEmail(trigger) === saved[category].email;
                const relaysMatch = relaySelectionOf(trigger, savedWiringFor(device)) === saved[category].relays;
                return (
                    (draft[category].email !== saved[category].email && emailMatches) ||
                    (draft[category].relays !== saved[category].relays && relaysMatch)
                );
            })
            .map((e) => ({ ...e, category })),
    );

    const allDevices = Array.from(
        new Set(ALERT_CATEGORIES.flatMap((c) => (triggerGroups.get(c) ?? []).map((e) => e.device))),
    );
    // Moving a terminal number rewrites relay entries fleet-wide, so every unit is a target then.
    const targets = numbersChanged ? allDevices : Array.from(new Set(entriesToMove.map((e) => e.device)));

    const commit = () => {
        setPending({
            targets,
            summary: [...changed.map((c) => ALERT_CATEGORY_LABELS[c]), ...(wiringChanged ? ["Relay wiring"] : [])].join(
                " · ",
            ),
            run: async () => {
                setBusy(true);
                const noEndpoint = new Set<string>();
                try {
                    await onSave((target) => {
                        target.notifyEmailOnCollision = draft.collision.email;
                        target.notifyEmailOnSpeeding = draft.speeding.email;
                        target.notifyEmailOnLeftPass = draft.left.email;
                        target.notifyEmailOnRightPass = draft.right.email;
                        target.collisionRelays = draft.collision.relays;
                        target.speedingRelays = draft.speeding.relays;
                        target.leftPassRelays = draft.left.relays;
                        target.rightPassRelays = draft.right.relays;
                        target.notificationEmailAddresses = draft.provisioning.emailAddresses.trim() || undefined;
                        target.webRelayIpAddress = draft.provisioning.relayIp.trim() || undefined;
                        target.hornRelayNumber = fleetWiring.horn.number;
                        target.hornRelayName = fleetWiring.horn.name;
                        target.hornRelaySnoozable = fleetWiring.horn.snoozable;
                        target.lightsRelayNumber = fleetWiring.lights.number;
                        target.lightsRelayName = fleetWiring.lights.name;
                        target.lightsRelaySnoozable = fleetWiring.lights.snoozable;
                    });

                    // One save per trigger even when both passes touch it, gathered here.
                    const toUpdate = new Set<EventTrigger>();
                    let rewired = 0;

                    /*
                     * Terminal numbers moved. The join configs store raw numbers, so every trigger
                     * still on the old wiring must be renumbered or its alerts keep firing the old
                     * terminal — the whole point of the change. Trucks whose endpoint pins its own
                     * numbers resolve to the same wiring before and after, and come through unmoved.
                     */
                    if (numbersChanged) {
                        for (const category of ALERT_CATEGORIES) {
                            for (const { trigger, device } of triggerGroups.get(category) ?? []) {
                                if (rewireRelayNumbers(trigger, savedWiringFor(device), draftWiringFor(device))) {
                                    toUpdate.add(trigger);
                                    rewired += 1;
                                }
                            }
                        }
                    }

                    for (const { trigger, device, category } of entriesToMove) {
                        if (draft[category].relays !== saved[category].relays) {
                            if (applyRelaySelection(trigger, draft[category].relays, relayFor(device), draftWiringFor(device))) {
                                toUpdate.add(trigger);
                            }
                        }

                        if (draft[category].email !== saved[category].email) {
                            const endpoint = endpointFor(device);
                            if (endpoint) {
                                if (applyEmailNotification(trigger, endpoint, draft[category].email)) toUpdate.add(trigger);
                            } else if (draft[category].email) {
                                noEndpoint.add(device.name ?? "a unit");
                            }
                        }
                    }

                    let pushed = 0;
                    for (const trigger of toUpdate) {
                        await EventTriggers.update(trigger);
                        pushed += 1;
                    }

                    const renumbered = rewired > 0 ? `, renumbering relay entries on ${rewired}` : "";
                    const base =
                        pushed > 0
                            ? `Defaults saved and pushed to ${pushed} trigger${pushed === 1 ? "" : "s"}${renumbered}`
                            : "Fleet defaults saved";
                    toast(
                        noEndpoint.size > 0
                            ? `${base}. ${noEndpoint.size} site${noEndpoint.size === 1 ? " has" : "s have"} no email endpoint, so ${noEndpoint.size === 1 ? "it was" : "they were"} not switched on.`
                            : base,
                    );
                    onPushed();
                } finally {
                    setBusy(false);
                    setPending(undefined);
                }
            },
        });
    };

    const recipients = uniqueRecipients(emailEndpoints);

    return (
        <>
            <Card>
                <CardHead title="Notification rules" note="org-wide" />
                <div className="mt-2">
                    {ALERT_CATEGORIES.map((category) => {
                        const count = (triggerGroups.get(category) ?? []).length;
                        return (
                            <FRow
                                stack
                                key={category}
                                title={ALERT_CATEGORY_LABELS[category]}
                                sub={count === 0 ? "No trigger of this kind in the fleet" : `${count} unit${count === 1 ? "" : "s"} have this alert`}
                            >
                                <span className="flex items-center gap-2">
                                    {/* Named controls, matching the device page: an unlabelled switch beside an
                                        alert's name reads as "is this alert on", which is Alerts & Relays'
                                        question, not this card's. */}
                                    <span className="text-[12px] text-[var(--muted)]">Relays</span>
                                    <Select
                                        value={String(draft[category].relays)}
                                        aria-label={`${ALERT_CATEGORY_LABELS[category]} relays`}
                                        onChange={(e) =>
                                            setDraft({
                                                ...draft,
                                                [category]: { ...draft[category], relays: Number(e.target.value) as RelaySelection },
                                            })
                                        }
                                    >
                                        {relayChoices(fleetWiring).map((c) => (
                                            <option key={c.label} value={String(c.value)}>
                                                {c.label}
                                            </option>
                                        ))}
                                    </Select>
                                    <span className="pl-1 text-[12px] text-[var(--muted)]">Email</span>
                                    <Toggle
                                        on={draft[category].email}
                                        onChange={(v) => setDraft({ ...draft, [category]: { ...draft[category], email: v } })}
                                        label={`Email on ${ALERT_CATEGORY_LABELS[category]}`}
                                    />
                                </span>
                            </FRow>
                        );
                    })}
                </div>
                <CardNote>
                    Who <b className="font-semibold text-[var(--text)]">hears about</b> each alert — email and relay closures —
                    not whether it fires; that lives under <b className="font-semibold text-[var(--text)]">Alerts &amp; Relays</b>.
                    Per-unit exceptions live on each truck under <b className="font-semibold text-[var(--text)]">Devices → Notifications</b>,
                    and a unit set differently keeps its own rule when this is saved.
                </CardNote>
            </Card>

            <Card>
                <CardHead title="Relay wiring" note="the standard build — which terminal is which" />
                <div className="mt-2">
                    {(["horn", "lights"] as const).map((slot) => (
                        <FRow
                            stack
                            key={slot}
                            title={slot === "horn" ? "Horn slot" : "Lights slot"}
                            sub={
                                slot === "horn"
                                    ? "The audible alert. Snooze lets drivers silence it from the cab."
                                    : "Fired alongside — beacons on the standard build, or whatever is wired there."
                            }
                        >
                            <span className="flex items-center gap-2">
                                <TextInput
                                    value={draft.wiring[slot].name}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            wiring: { ...draft.wiring, [slot]: { ...draft.wiring[slot], name: e.target.value } },
                                        })
                                    }
                                    className="w-[130px]"
                                    placeholder={DEFAULT_RELAY_WIRING[slot].name}
                                    aria-label={`${slot === "horn" ? "Horn" : "Lights"} slot name`}
                                />
                                <Select
                                    value={String(draft.wiring[slot].number)}
                                    aria-label={`${slot === "horn" ? "Horn" : "Lights"} slot relay number`}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            wiring: {
                                                ...draft.wiring,
                                                [slot]: { ...draft.wiring[slot], number: Number(e.target.value) },
                                            },
                                        })
                                    }
                                >
                                    {[1, 2, 3, 4].map((n) => (
                                        <option key={n} value={String(n)}>
                                            Relay {n}
                                        </option>
                                    ))}
                                </Select>
                                <span className="flex items-center gap-1.5">
                                    <span className="text-[12px] text-[var(--muted)]">Snooze</span>
                                    <Toggle
                                        on={draft.wiring[slot].snoozable}
                                        onChange={(v) =>
                                            setDraft({
                                                ...draft,
                                                wiring: {
                                                    ...draft.wiring,
                                                    [slot]: { ...draft.wiring[slot], snoozable: v },
                                                },
                                            })
                                        }
                                        label={`${slot === "horn" ? "Horn" : "Lights"} slot snoozable`}
                                    />
                                </span>
                            </span>
                        </FRow>
                    ))}
                </div>
                {!wiringValid && (
                    <div className="px-5 pb-3">
                        <Note tone="crit">
                            Both slots point at relay {draft.wiring.horn.number}. Give each its own terminal before saving.
                        </Note>
                    </div>
                )}
                <CardNote>
                    Names are what drivers and these pages call each relay; Snooze puts a snooze button for it in the cab.
                    A truck wired differently overrides all of this on its own Notifications tab. Moving a terminal number
                    here renumbers the relay entries on every fleet-following truck's triggers when saved.
                </CardNote>
            </Card>

            <Card>
                <CardHead title="Provisioning" note="used when a site is missing an endpoint" />
                <div className="mt-2">
                    {/* Stacked, not FRow: these two carry long hints and free-text inputs, and the
                        side-by-side row wrapped the hint into a squashed sliver beside an outsized
                        box. Label and hint get the card's full width; the input sits under them,
                        sized to what it holds. */}
                    <ProvisionRow
                        title="Notification addresses"
                        sub="Comma separated. Given to any site that has no email endpoint when one is needed."
                    >
                        <TextInput
                            value={draft.provisioning.emailAddresses}
                            onChange={(e) =>
                                setDraft({ ...draft, provisioning: { ...draft.provisioning, emailAddresses: e.target.value } })
                            }
                            className="w-full max-w-[420px]"
                            placeholder="safety@example.com, ops@example.com"
                            aria-label="Notification email addresses"
                        />
                    </ProvisionRow>
                    <ProvisionRow
                        title="Relay board address"
                        sub="The standard build puts every truck's relay on the same address."
                    >
                        <TextInput
                            value={draft.provisioning.relayIp}
                            onChange={(e) =>
                                setDraft({ ...draft, provisioning: { ...draft.provisioning, relayIp: e.target.value } })
                            }
                            className="w-[180px] font-mono"
                            placeholder="192.168.1.2"
                            aria-label="Relay board IP address"
                        />
                    </ProvisionRow>
                </div>
                <CardNote>
                    Left blank, a site without an email endpoint keeps none rather than being given one that reaches nobody —
                    an endpoint with no addresses looks configured while notifying no one.
                </CardNote>
            </Card>

            <Card>
                <CardHead title="Who gets notified" note="every address on the fleet's email endpoints" />
                {recipients.length === 0 ? (
                    <p className="px-5 pb-5 pt-3 text-[13px] text-[var(--muted)]">
                        No email recipients are configured anywhere in the fleet, so switching email on will not reach anyone
                        until an endpoint has addresses.
                    </p>
                ) : (
                    <div className="mt-2">
                        {recipients.map((r) => (
                            <FRow
                                key={r.address.toLowerCase()}
                                title={r.address}
                                sub={
                                    r.endpointCount === 1
                                        ? r.endpointNames[0]
                                        : r.endpointCount === emailEndpoints.length
                                          ? `All ${emailEndpoints.length} email endpoints`
                                          : `${r.endpointCount} of ${emailEndpoints.length} email endpoints`
                                }
                            >
                                <Tag tone="safe">Notified</Tag>
                            </FRow>
                        ))}
                    </div>
                )}
                <CardNote>Addresses live on the endpoint itself, edited on the main dashboard.</CardNote>
            </Card>

            <SaveBar
                show={dirty}
                busy={busy}
                canSave={wiringValid}
                label="Unsaved · fleet defaults"
                onSave={commit}
                onDiscard={() => setDraft(saved)}
            />

            {pending && (
                <ApplyToFleetDialog
                    open
                    onClose={() => setPending(undefined)}
                    title="Save notification defaults"
                    summary={pending.summary}
                    targets={pending.targets}
                    untouched={allDevices.filter((d) => !pending.targets.includes(d))}
                    onConfirm={pending.run}
                />
            )}
        </>
    );
};

